import { AccountsApiError } from '@/lib/apiAccounts';
import {
  createDiscussionCommentApi,
  deleteDiscussionCommentApi,
  fetchDiscussionSummaries,
  fetchDiscussionThreadAuthenticated,
  fetchDiscussionThreadPublic,
  setDiscussionVoteApi,
  updateDiscussionCommentApi,
  type ApiDiscussionComment,
  type ApiDiscussionPreviewComment,
  type ApiDiscussionThreadFetchOptions,
  type ApiDiscussionThreadResponse,
  type ApiDiscussionVoteValue,
} from '@/lib/planningDiscussionsApi';

export interface PlanningDiscussionAuthor {
  displayName: string;
}

export interface PlanningDiscussionVotes {
  upCount: number;
  downCount: number;
  score: number;
  currentUserVote: 'up' | 'down' | null;
}

export type PlanningDiscussionStatus = 'visible' | 'deleted' | 'hidden';

export interface PlanningDiscussionComment {
  id: string;
  reference: string;
  parentId?: string | null;
  body: string;
  status: PlanningDiscussionStatus;
  createdAt: string;
  updatedAt: string;
  author: PlanningDiscussionAuthor;
  votes: PlanningDiscussionVotes;
  canEdit: boolean;
  canDelete: boolean;
}

export interface PlanningDiscussionThreadNode extends PlanningDiscussionComment {
  replies: PlanningDiscussionThreadNode[];
}

export interface PlanningDiscussionPreviewComment {
  id: string;
  body: string;
  status: PlanningDiscussionStatus;
  createdAt: string;
  replyCount: number;
  author: PlanningDiscussionAuthor;
  /** Net vote score; used to order the "top" comment in card previews */
  score?: number;
}

export interface PlanningDiscussionSummary {
  reference: string;
  commentCount: number;
  threadCount: number;
  previewComments: PlanningDiscussionPreviewComment[];
}

export interface PlanningDiscussionThreadPayload extends PlanningDiscussionSummary {
  hasMore: boolean;
  nextBefore: string | null;
  moderation?: PlanningDiscussionModerationResult;
  comments: PlanningDiscussionThreadNode[];
}

export interface PlanningDiscussionModerationResult {
  commentId: string;
  status: PlanningDiscussionStatus;
  level: 'clear' | 'held';
  reasons: string[];
  message: string;
}

export const PLANNING_DISCUSSIONS_UPDATED_EVENT = 'cityscope:planning-discussions-updated';

const SUMMARY_BATCH_SIZE = 80;

const threadCache = new Map<string, PlanningDiscussionThreadPayload>();
const summaryCache = new Map<string, PlanningDiscussionSummary>();
const threadPromiseCache = new Map<string, Promise<PlanningDiscussionThreadPayload>>();
const pendingSummaryRefs = new Set<string>();
const pendingSummaryResolvers: Array<{
  references: string[];
  resolve: (value: PlanningDiscussionSummary[]) => void;
  reject: (reason?: unknown) => void;
}> = [];
let scheduledSummaryFlush: ReturnType<typeof setTimeout> | null = null;

function normalizeReference(reference: string): string {
  return String(reference || '').replace(/\s+/g, '').trim().toUpperCase();
}

function sortNewestFirst(
  left: Pick<PlanningDiscussionComment, 'createdAt'>,
  right: Pick<PlanningDiscussionComment, 'createdAt'>
): number {
  return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
}

function sortOldestFirst(
  left: Pick<PlanningDiscussionComment, 'createdAt'>,
  right: Pick<PlanningDiscussionComment, 'createdAt'>
): number {
  return new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime();
}

function createEmptySummary(reference: string): PlanningDiscussionSummary {
  return {
    reference,
    commentCount: 0,
    threadCount: 0,
    previewComments: [],
  };
}

function createEmptyThreadPayload(reference: string): PlanningDiscussionThreadPayload {
  return {
    ...createEmptySummary(reference),
    hasMore: false,
    nextBefore: null,
    comments: [],
  };
}

function chunkReferences(references: string[], size: number): string[][] {
  const chunks: string[][] = [];
  for (let index = 0; index < references.length; index += size) {
    chunks.push(references.slice(index, index + size));
  }
  return chunks;
}

function mapApiComment(comment: ApiDiscussionComment, fallbackReference: string): PlanningDiscussionComment {
  return {
    id: String(comment.id || '').trim(),
    reference: normalizeReference(comment.reference || fallbackReference),
    parentId: typeof comment.parent_id === 'string' && comment.parent_id.trim() ? comment.parent_id.trim() : null,
    body: typeof comment.body === 'string' ? comment.body : '',
    status: comment.status || 'visible',
    createdAt: comment.created_at || new Date().toISOString(),
    updatedAt: comment.updated_at || comment.created_at || new Date().toISOString(),
    author: {
      displayName:
        typeof comment.author?.display_name === 'string' && comment.author.display_name.trim()
          ? comment.author.display_name.trim()
          : 'City Scope user',
    },
    votes: {
      upCount: Number(comment.votes?.up_count || 0),
      downCount: Number(comment.votes?.down_count || 0),
      score: Number(comment.votes?.score || 0),
      currentUserVote:
        comment.votes?.current_user_vote === 'up' || comment.votes?.current_user_vote === 'down'
          ? comment.votes.current_user_vote
          : null,
    },
    canEdit: Boolean(comment.can_edit),
    canDelete: Boolean(comment.can_delete),
  };
}

function mapApiPreviewComment(comment: ApiDiscussionPreviewComment): PlanningDiscussionPreviewComment {
  const apiVotes = (comment as { votes?: { score?: number } }).votes;
  return {
    id: String(comment.id || '').trim(),
    body: typeof comment.body === 'string' ? comment.body : '',
    status: comment.status || 'visible',
    createdAt: comment.created_at || new Date().toISOString(),
    replyCount: Number(comment.reply_count || 0),
    author: {
      displayName:
        typeof comment.author?.display_name === 'string' && comment.author.display_name.trim()
          ? comment.author.display_name.trim()
          : 'City Scope user',
    },
    score: Number(apiVotes?.score || 0),
  };
}

/** Sort by net score desc; tie-break by earliest createdAt (the first comment added wins ties). */
function sortPreviewCommentsByTopUpvote(
  comments: PlanningDiscussionPreviewComment[]
): PlanningDiscussionPreviewComment[] {
  return [...comments].sort((a, b) => {
    const scoreA = Number(a.score || 0);
    const scoreB = Number(b.score || 0);
    if (scoreA !== scoreB) return scoreB - scoreA;
    const timeA = new Date(a.createdAt).getTime();
    const timeB = new Date(b.createdAt).getTime();
    return timeA - timeB;
  });
}

function buildThread(comments: PlanningDiscussionComment[]): PlanningDiscussionThreadNode[] {
  if (!comments.length) {
    return [];
  }

  const sorted = [...comments].sort(sortOldestFirst);
  const nodes = new Map<string, PlanningDiscussionThreadNode>();
  for (const comment of sorted) {
    nodes.set(comment.id, { ...comment, replies: [] });
  }

  const roots: PlanningDiscussionThreadNode[] = [];
  for (const comment of sorted) {
    const node = nodes.get(comment.id);
    if (!node) {
      continue;
    }

    const parent = comment.parentId ? nodes.get(comment.parentId) : null;
    if (parent) {
      parent.replies.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortReplies = (items: PlanningDiscussionThreadNode[]) => {
    items.sort(sortOldestFirst);
    items.forEach((item) => sortReplies(item.replies));
  };

  roots.sort(sortNewestFirst);
  roots.forEach((root) => sortReplies(root.replies));
  return roots;
}

function countDescendants(node: PlanningDiscussionThreadNode): number {
  return node.replies.reduce((total, reply) => total + 1 + countDescendants(reply), 0);
}

function buildPreviewComments(thread: PlanningDiscussionThreadNode[]): PlanningDiscussionPreviewComment[] {
  const previews: PlanningDiscussionPreviewComment[] = thread.map((comment) => ({
    id: comment.id,
    body: comment.body,
    status: comment.status,
    createdAt: comment.createdAt,
    replyCount: countDescendants(comment),
    author: comment.author,
    score: Number(comment.votes?.score || 0),
  }));
  return sortPreviewCommentsByTopUpvote(previews).slice(0, 3);
}

function summarizeThreadPayload(payload: PlanningDiscussionThreadPayload): PlanningDiscussionSummary {
  return {
    reference: payload.reference,
    commentCount: payload.commentCount,
    threadCount: payload.threadCount,
    previewComments: payload.previewComments,
  };
}

function mergeThreadPayload(
  existing: PlanningDiscussionThreadPayload | undefined,
  incoming: PlanningDiscussionThreadPayload
): PlanningDiscussionThreadPayload {
  if (!existing || existing.reference !== incoming.reference) {
    return incoming;
  }

  const seenRootIds = new Set(existing.comments.map((comment) => comment.id));
  const mergedComments = [
    ...existing.comments,
    ...incoming.comments.filter((comment) => !seenRootIds.has(comment.id)),
  ];

  return {
    ...incoming,
    comments: mergedComments,
    previewComments: buildPreviewComments(mergedComments),
  };
}

function mapThreadResponse(response: ApiDiscussionThreadResponse): PlanningDiscussionThreadPayload {
  const reference = normalizeReference(response.reference_no);
  const flatComments = Array.isArray(response.comments)
    ? response.comments.map((comment) => mapApiComment(comment, reference))
    : [];
  const comments = buildThread(flatComments);
  const commentCount = Number.isFinite(response.comment_count) ? Number(response.comment_count) : flatComments.length;
  const threadCount = Number.isFinite(response.thread_count) ? Number(response.thread_count) : comments.length;

  return {
    reference,
    commentCount,
    threadCount,
    previewComments: buildPreviewComments(comments),
    hasMore: Boolean(response.has_more),
    nextBefore: typeof response.next_before === 'string' && response.next_before.trim() ? response.next_before : null,
    moderation: response.moderation
      ? {
          commentId: String(response.moderation.comment_id || '').trim(),
          status: response.moderation.status || 'visible',
          level: response.moderation.level || 'clear',
          reasons: Array.isArray(response.moderation.reasons)
            ? response.moderation.reasons.filter((reason): reason is string => typeof reason === 'string')
            : [],
          message:
            typeof response.moderation.message === 'string'
              ? response.moderation.message
              : '',
        }
      : undefined,
    comments,
  };
}

function applyThreadPayload(payload: PlanningDiscussionThreadPayload): PlanningDiscussionThreadPayload {
  threadCache.set(payload.reference, payload);
  summaryCache.set(payload.reference, summarizeThreadPayload(payload));
  emitDiscussionUpdate(payload.reference);
  return payload;
}

function applySummary(summary: PlanningDiscussionSummary): PlanningDiscussionSummary {
  const normalized: PlanningDiscussionSummary = {
    ...summary,
    previewComments: sortPreviewCommentsByTopUpvote(summary.previewComments),
  };
  summaryCache.set(normalized.reference, normalized);
  emitDiscussionUpdate(normalized.reference);
  return normalized;
}

function emitDiscussionUpdate(reference: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(PLANNING_DISCUSSIONS_UPDATED_EVENT, {
      detail: { reference: normalizeReference(reference) },
    })
  );
}

async function fetchThread(
  reference: string,
  preferAuthenticated: boolean,
  options: ApiDiscussionThreadFetchOptions & { append?: boolean } = {}
): Promise<PlanningDiscussionThreadPayload> {
  const normalizedReference = normalizeReference(reference);
  if (!normalizedReference) {
    return createEmptyThreadPayload('');
  }

  const requestOptions: ApiDiscussionThreadFetchOptions = {};
  if (typeof options.limit === 'number' && Number.isFinite(options.limit) && options.limit > 0) {
    requestOptions.limit = options.limit;
  }
  if (typeof options.before === 'string' && options.before.trim()) {
    requestOptions.before = options.before.trim();
  }
  if (typeof options.council === 'string' && options.council.trim()) {
    requestOptions.council = options.council.trim();
  }
  const hasRequestOptions = Object.keys(requestOptions).length > 0;
  const promiseKey = [
    normalizedReference,
    preferAuthenticated ? 'auth' : 'public',
    requestOptions.limit || '',
    requestOptions.before || '',
    requestOptions.council || '',
    options.append ? 'append' : 'replace',
  ].join(':');
  const existingPromise = threadPromiseCache.get(promiseKey);
  if (existingPromise) {
    return existingPromise;
  }

  const nextPromise = (async () => {
    let response: ApiDiscussionThreadResponse;
    if (preferAuthenticated) {
      try {
        response = hasRequestOptions
          ? await fetchDiscussionThreadAuthenticated(normalizedReference, requestOptions)
          : await fetchDiscussionThreadAuthenticated(normalizedReference);
      } catch (error) {
        const maybeAccountsError = error as AccountsApiError | undefined;
        if (maybeAccountsError?.status !== 401 && maybeAccountsError?.status !== 403) {
          throw error;
        }
        response = hasRequestOptions
          ? await fetchDiscussionThreadPublic(normalizedReference, requestOptions)
          : await fetchDiscussionThreadPublic(normalizedReference);
      }
    } else {
      response = hasRequestOptions
        ? await fetchDiscussionThreadPublic(normalizedReference, requestOptions)
        : await fetchDiscussionThreadPublic(normalizedReference);
    }

    const mappedPayload = mapThreadResponse(response);
    const payload = options.append
      ? mergeThreadPayload(threadCache.get(normalizedReference), mappedPayload)
      : mappedPayload;
    return applyThreadPayload(payload);
  })();

  threadPromiseCache.set(promiseKey, nextPromise);

  try {
    return await nextPromise;
  } finally {
    threadPromiseCache.delete(promiseKey);
  }
}

async function flushSummaryHydration(): Promise<void> {
  const references = [...pendingSummaryRefs];
  pendingSummaryRefs.clear();
  scheduledSummaryFlush = null;
  const resolvers = pendingSummaryResolvers.splice(0, pendingSummaryResolvers.length);

  try {
    if (references.length > 0) {
      const seen = new Set<string>();

      for (const chunk of chunkReferences(references, SUMMARY_BATCH_SIZE)) {
        const response = await fetchDiscussionSummaries(chunk);
        // Older CDN entries may still contain the proxy's former empty 200 fallback.
        // Do not cache those zeros as real discussion counts.
        if (response.partial === true || !Array.isArray(response.summaries)) {
          throw new Error('Incomplete discussion summary response');
        }

        for (const summary of response.summaries) {
          const normalizedReference = normalizeReference(summary.reference_no);
          seen.add(normalizedReference);
          applySummary({
            reference: normalizedReference,
            commentCount: Number(summary.comment_count || 0),
            threadCount: Number(summary.thread_count || 0),
            previewComments: Array.isArray(summary.preview_comments)
              ? summary.preview_comments.map((comment) => mapApiPreviewComment(comment))
              : [],
          });
        }
      }

      for (const reference of references) {
        if (!seen.has(reference) && !summaryCache.has(reference)) {
          applySummary(createEmptySummary(reference));
        }
      }
    }

    for (const pending of resolvers) {
      pending.resolve(pending.references.map((reference) => getDiscussionSummary(reference)));
    }
  } catch (error) {
    for (const pending of resolvers) {
      pending.reject(error);
    }
  }
}

function scheduleSummaryHydration(): void {
  if (scheduledSummaryFlush) {
    return;
  }

  scheduledSummaryFlush = setTimeout(() => {
    void flushSummaryHydration();
  }, 0);
}

export async function hydrateDiscussion(
  reference: string,
  options: { preferAuthenticated?: boolean; before?: string | null; append?: boolean; limit?: number; council?: string | null } = {}
): Promise<PlanningDiscussionThreadPayload> {
  return fetchThread(reference, Boolean(options.preferAuthenticated), {
    before: options.before,
    append: options.append,
    limit: options.limit,
    council: options.council,
  });
}

export async function hydrateDiscussionSummary(reference: string): Promise<PlanningDiscussionSummary> {
  const [summary] = await hydrateDiscussionSummaries([reference]);
  return summary || createEmptySummary(normalizeReference(reference));
}

export async function hydrateDiscussionSummaries(
  references: string[]
): Promise<PlanningDiscussionSummary[]> {
  const normalizedReferences = Array.from(
    new Set(references.map((reference) => normalizeReference(reference)).filter(Boolean))
  );

  if (normalizedReferences.length === 0) {
    return [];
  }

  const missingReferences = normalizedReferences.filter((reference) => !summaryCache.has(reference));
  if (missingReferences.length === 0) {
    return normalizedReferences.map((reference) => getDiscussionSummary(reference));
  }

  return new Promise<PlanningDiscussionSummary[]>((resolve, reject) => {
    pendingSummaryResolvers.push({
      references: normalizedReferences,
      resolve,
      reject,
    });

    missingReferences.forEach((reference) => pendingSummaryRefs.add(reference));
    scheduleSummaryHydration();
  });
}

export async function addDiscussionComment(input: {
  reference: string;
  body: string;
  parentId?: string | null;
  authorDisplayName?: string | null;
  council?: string | null;
}): Promise<PlanningDiscussionThreadPayload> {
  const normalizedReference = normalizeReference(input.reference);
  const response = await createDiscussionCommentApi(normalizedReference, {
    body: String(input.body || '').trim(),
    parent_id:
      typeof input.parentId === 'string' && input.parentId.trim() ? input.parentId.trim() : null,
    council: input.council,
  });
  return applyThreadPayload(mapThreadResponse(response));
}

export async function updateDiscussionComment(input: {
  commentId: string;
  body: string;
}): Promise<PlanningDiscussionThreadPayload> {
  const response = await updateDiscussionCommentApi(String(input.commentId || '').trim(), {
    body: String(input.body || '').trim(),
  });
  return applyThreadPayload(mapThreadResponse(response));
}

export async function deleteDiscussionComment(input: {
  commentId: string;
}): Promise<PlanningDiscussionThreadPayload> {
  const response = await deleteDiscussionCommentApi(String(input.commentId || '').trim());
  return applyThreadPayload(mapThreadResponse(response));
}

export async function toggleDiscussionVote(input: {
  commentId: string;
  value: 'up' | 'down';
}): Promise<PlanningDiscussionThreadPayload> {
  const existingPayload = threadCache.get(getDiscussionReferenceForComment(input.commentId));
  const currentVote = existingPayload
    ? findDiscussionComment(existingPayload.comments, input.commentId)?.votes.currentUserVote
    : null;
  const nextValue: ApiDiscussionVoteValue = currentVote === input.value ? null : input.value;
  const response = await setDiscussionVoteApi(String(input.commentId || '').trim(), nextValue);
  return applyThreadPayload(mapThreadResponse(response));
}

function findDiscussionComment(
  nodes: PlanningDiscussionThreadNode[],
  commentId: string
): PlanningDiscussionThreadNode | null {
  for (const node of nodes) {
    if (node.id === commentId) {
      return node;
    }
    const nested = findDiscussionComment(node.replies, commentId);
    if (nested) {
      return nested;
    }
  }
  return null;
}

function getDiscussionReferenceForComment(commentId: string): string {
  for (const [reference, payload] of threadCache.entries()) {
    if (findDiscussionComment(payload.comments, commentId)) {
      return reference;
    }
  }
  return '';
}

export function getDiscussionThread(reference: string): PlanningDiscussionThreadNode[] {
  const normalizedReference = normalizeReference(reference);
  return threadCache.get(normalizedReference)?.comments || [];
}

export function getDiscussionThreadPayload(reference: string): PlanningDiscussionThreadPayload {
  const normalizedReference = normalizeReference(reference);
  return threadCache.get(normalizedReference) || createEmptyThreadPayload(normalizedReference);
}

export function getDiscussionSummary(reference: string): PlanningDiscussionSummary {
  const normalizedReference = normalizeReference(reference);
  return summaryCache.get(normalizedReference) || createEmptySummary(normalizedReference);
}

export function getDiscussionCount(reference: string): number {
  return getDiscussionSummary(reference).commentCount;
}

export function getDiscussionThreadCount(reference: string): number {
  return getDiscussionSummary(reference).threadCount;
}

export function getDiscussionPreviewComments(reference: string): PlanningDiscussionPreviewComment[] {
  return getDiscussionSummary(reference).previewComments;
}

export function getDiscussionVoteScore(comment: Pick<PlanningDiscussionComment, 'votes'>): number {
  return Number(comment.votes?.score || 0);
}

export function getDiscussionUserVote(
  comment: Pick<PlanningDiscussionComment, 'votes'>
): 'up' | 'down' | null {
  return comment.votes?.currentUserVote === 'up' || comment.votes?.currentUserVote === 'down'
    ? comment.votes.currentUserVote
    : null;
}

export function subscribeToDiscussionUpdates(callback: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const handleCustomEvent = () => callback();
  window.addEventListener(PLANNING_DISCUSSIONS_UPDATED_EVENT, handleCustomEvent);

  return () => {
    window.removeEventListener(PLANNING_DISCUSSIONS_UPDATED_EVENT, handleCustomEvent);
  };
}

export function clearPlanningDiscussionCache(): void {
  threadCache.clear();
  summaryCache.clear();
  threadPromiseCache.clear();
  pendingSummaryRefs.clear();
  pendingSummaryResolvers.splice(0, pendingSummaryResolvers.length);
  if (scheduledSummaryFlush) {
    clearTimeout(scheduledSummaryFlush);
    scheduledSummaryFlush = null;
  }
}
